import { useQueryFetcher } from '@/lib/query/useQueryFetcher';
import useIsMemberAuthorized from '@/hooks/useIsMemberAuthorized';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    ConsumerDataResponse,
    WebhookBody,
    createConsumer,
    createConsumerToken,
    saveConsumerWebhook,
    deleteConsumerWebhook,
} from '@/api/consumerApi';
import { updateApiCache, makeApiKey } from '@/lib/query/fetcherCache';
import { queryClient } from '@/lib/query/queryClient';
import { suspendActiveSubscription, unpauseActiveSubscription } from '@/api/paymentsApi';
import { API_ROUTES } from '@/lib/http/httpRequest';
import { HT_API_ROUTES } from '@/lib/http/hypertrackerHttpRequest';
import { APP_FOR_PAYMENT, HT_PAYMENT_PROVIDER } from '@/state/payments/paymentsTypes';

export type ActiveSubscription = {
    subscriptionUuid: string;
    effectiveAt: number;
    expiresAt: number;
    productCode: string;
    subscriptionPlanDuration: string;
    applicationCode: string;
    subscriptionStatus: string;
    paymentProvider: HT_PAYMENT_PROVIDER;
};

const useConsumerDataQuery = () => {
    const isMemberAuthorized = useIsMemberAuthorized();

    useEffect(() => {
        if (!isMemberAuthorized) {
            queryClient.removeQueries({
                queryKey: makeApiKey(HT_API_ROUTES.CONSUMER.ROOT, undefined),
            });
        }
    }, [isMemberAuthorized]);

    const consumerDataQuery = useQueryFetcher<ConsumerDataResponse>({
        mode: 'api',
        apiPath: HT_API_ROUTES.CONSUMER.ROOT,
        isAuthorized: isMemberAuthorized,
        enabled: isMemberAuthorized,
    });

    const activeSubscriptionsQuery = useQueryFetcher<Record<string, ActiveSubscription[]>>({
        mode: 'api',
        apiPath: `${API_ROUTES.PAYMENTS}/subscriptions/active`,
        isAuthorized: isMemberAuthorized,
        enabled: isMemberAuthorized,
        axiosInstance: 'payment',
    });

    const activeSubscriptions = useMemo(() => {
        return activeSubscriptionsQuery?.data?.subscriptions ?? [];
    }, [activeSubscriptionsQuery?.data]);

    const consumerData = useMemo(() => {
        if (!isMemberAuthorized) return null;
        return consumerDataQuery?.data ?? null;
    }, [consumerDataQuery?.data, isMemberAuthorized]);

    const activeApiSubscription = useMemo(() => {
        if (!activeSubscriptions || !Array.isArray(activeSubscriptions)) return null;
        const apiSubs = activeSubscriptions.filter(
            (item) => item.applicationCode === APP_FOR_PAYMENT.HYPERTRACKER_API,
        );
        if (apiSubs.length === 0) return null;
        if (apiSubs.length === 1) return apiSubs?.[0];

        let result: any = null;

        apiSubs.forEach((item) => {
            let start: any = item?.effectiveAt;
            const end = item?.expiresAt;
            if (
                !start &&
                consumerData?.subscription?.sourceId === item.subscriptionUuid &&
                consumerData?.subscription?.effectiveAt
            ) {
                start = consumerData.subscription.effectiveAt;
            }

            const now = new Date().getTime();

            if (
                start &&
                end &&
                new Date(start).getTime() <= now &&
                new Date(end).getTime() >= now
            ) {
                result = item;
            }
        });

        return result;
    }, [activeSubscriptions, consumerData]);

    const isSubscriptionActive = useCallback(
        (subscriptionUuid: string): boolean => {
            if (!subscriptionUuid) return false;
            const subscription = activeSubscriptions.find(
                (sub) => sub.subscriptionUuid === subscriptionUuid,
            );
            if (!subscription) return false;
            return subscription.subscriptionStatus !== 'suspended';
        },
        [activeSubscriptions],
    );

    const isPaidWithCrypto = useCallback(
        (subscriptionUuid: string) => {
            if (!subscriptionUuid) return false;
            const subscription = activeSubscriptions.find(
                (sub) => sub.subscriptionUuid === subscriptionUuid,
            );

            if (!subscription) return false;
            return subscription.paymentProvider !== HT_PAYMENT_PROVIDER.STRIPE;
        },
        [activeSubscriptions],
    );

    const isCryptoSubscription = useMemo(() => {
        if (!consumerData?.subscription?.sourceId) return false;
        return isPaidWithCrypto(consumerData.subscription.sourceId);
    }, [consumerData, isPaidWithCrypto]);

    const isSubscriptionPaused = useMemo(() => {
        return Boolean(
            consumerData?.subscription?.source === 'payment-service' &&
            !isSubscriptionActive(consumerData?.subscription?.sourceId as string),
        );
    }, [consumerData, isSubscriptionActive]);

    const isRenewing = useMemo(() => {
        return (
            Boolean(consumerData?.tokens?.expiresAt) &&
            !isCryptoSubscription &&
            !isSubscriptionPaused
        );
    }, [consumerData, isCryptoSubscription, isSubscriptionPaused]);

    const onCreateConsumer = useCallback(async () => {
        setTokenActionLoading(true);
        try {
            await createConsumer();
            await consumerDataQuery.refetch();
        } finally {
            setTokenActionLoading(false);
        }
    }, [consumerDataQuery]);

    const onCreateConsumerToken = useCallback(async () => {
        setTokenActionLoading(true);
        try {
            const { data } = await createConsumerToken();
            updateApiCache<ConsumerDataResponse>(HT_API_ROUTES.CONSUMER.ROOT, undefined, (prev) =>
                prev ? { ...prev, accessToken: data.accessToken } : prev,
            );
        } finally {
            setTokenActionLoading(false);
        }
    }, []);

    const [subscriptionActionLoading, setSubscriptionActionLoading] = useState(false);
    const [tokenActionLoading, setTokenActionLoading] = useState(false);
    const [webhookActionLoading, setWebhookActionLoading] = useState(false);

    const saveWebhook = useCallback(
        async (body: WebhookBody) => {
            setWebhookActionLoading(true);
            try {
                await saveConsumerWebhook(body);
                await consumerDataQuery.refetch();
            } catch (error) {
                console.error('[saveWebhook]', error);
                throw error;
            } finally {
                setWebhookActionLoading(false);
            }
        },
        [consumerDataQuery],
    );

    const deleteWebhook = useCallback(async () => {
        setWebhookActionLoading(true);
        try {
            await deleteConsumerWebhook();
            await consumerDataQuery.refetch();
        } catch (error) {
            console.error('[deleteWebhook]', error);
            throw error;
        } finally {
            setWebhookActionLoading(false);
        }
    }, [consumerDataQuery]);

    const postponePlan = useCallback(async () => {
        if (
            consumerData?.subscription.source === 'payment-service' &&
            consumerData?.subscription.sourceId
        ) {
            setSubscriptionActionLoading(true);
            await suspendActiveSubscription({
                subscriptionUuid: consumerData?.subscription?.sourceId,
            });
            await new Promise<void>((resolve) => {
                setTimeout(async () => {
                    await activeSubscriptionsQuery.refetch();
                    setSubscriptionActionLoading(false);
                    resolve();
                }, 1000);
            });
        }
    }, [consumerData, activeSubscriptionsQuery]);

    const resumePlan = useCallback(async () => {
        if (
            consumerData?.subscription.source === 'payment-service' &&
            consumerData?.subscription.sourceId
        ) {
            setSubscriptionActionLoading(true);
            await unpauseActiveSubscription({
                subscriptionUuid: consumerData?.subscription?.sourceId,
            });
            await new Promise<void>((resolve) => {
                setTimeout(async () => {
                    await activeSubscriptionsQuery.refetch();
                    setSubscriptionActionLoading(false);
                    resolve();
                }, 1000);
            });
        }
    }, [consumerData, activeSubscriptionsQuery]);

    return {
        consumer: consumerData,
        activeApiSubscription,
        activeSubscriptions,
        consumerDataLoading: consumerDataQuery?.isLoading,
        onCreateConsumer,
        onCreateConsumerToken,
        postponePlan,
        resumePlan,
        isSubscriptionActive,
        subscriptionActionLoading,
        tokenActionLoading,
        isPaidWithCrypto,
        isCryptoSubscription,
        isSubscriptionPaused,
        isRenewing,
        webhook: consumerData?.webhook?.active ? consumerData.webhook : null,
        saveWebhook,
        deleteWebhook,
        webhookActionLoading,
    };
};

export default useConsumerDataQuery;
